import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot, setDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export type Payment = {
  date: string;
  amount: number;
  receipt: string;
  paymentType: string;
  isCommissionPaid: boolean;
};

export type Lot = {
  id: string;
  status: 'disponible' | 'vendido' | 'crédito' | 'devuelto';
  seller: string;
  buyerName: string;
  buyerDPI: string;
  phoneNumber: string;
  depositSlip: string;
  receiptNumber: string;
  numeroDocumento?: string; // No. Doc. del Banco (nuevo campo)
  transferencia?: string; // No. Autorización (nuevo campo)
  saleDate: string;
  paymentType: 'contado' | 'pagos';
  lotValue: number;
  totalValue: number;
  amountPaid: number;
  numPayments?: number;
  payments: Payment[];
  originalSeller?: string;
};

export function useLots() {
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar lotes desde Firestore
  const fetchLots = useCallback(async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'lots'));
      const lotsData: Lot[] = [];
      querySnapshot.forEach((doc) => {
        lotsData.push({ id: doc.id, ...doc.data() } as Lot);
      });
      setLots(lotsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching lots:', err);
      setError('Error al cargar lotes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Suscripción en tiempo real a los lotes
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'lots'), (snapshot) => {
      const lotsData: Lot[] = [];
      snapshot.forEach((doc) => {
        lotsData.push({ id: doc.id, ...doc.data() } as Lot);
      });
      setLots(lotsData);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to lots:', err);
      setError('Error al sincronizar lotes');
    });

    return () => unsubscribe();
  }, []);

  // Agregar un lote
  const addLot = useCallback(async (lot: Omit<Lot, 'id'>) => {
    try {
      const docRef = await addDoc(collection(db, 'lots'), lot);
      const newLot = { id: docRef.id, ...lot };
      return docRef.id;
    } catch (err) {
      console.error('Error adding lot:', err);
      setError('Error al agregar lote');
      throw err;
    }
  }, []);

  // Editar un lote
  const updateLot = useCallback(async (id: string, updates: Partial<Lot>) => {
    try {
      await updateDoc(doc(db, 'lots', id), updates);
    } catch (err) {
      console.error('Error updating lot:', err);
      setError('Error al actualizar lote');
      throw err;
    }
  }, []);

  // Eliminar un lote
  const deleteLot = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'lots', id));
    } catch (err) {
      console.error('Error deleting lot:', err);
      setError('Error al eliminar lote');
      throw err;
    }
  }, []);

  // Registrar un pago
  const addPayment = useCallback(async (id: string, payment: Payment) => {
    try {
      const lot = lots.find(l => l.id === id);
      if (!lot) throw new Error('Lote no encontrado');

      const newPayments = [...lot.payments, payment];
      const newAmountPaid = lot.amountPaid + payment.amount;
      let newStatus = lot.status;
      if (lot.paymentType === 'contado' || newAmountPaid >= lot.totalValue) {
        newStatus = 'vendido';
      } else if (lot.paymentType === 'pagos') {
        newStatus = 'crédito';
      }

      await updateDoc(doc(db, 'lots', id), {
        payments: newPayments,
        amountPaid: newAmountPaid,
        status: newStatus,
      });
    } catch (err) {
      console.error('Error adding payment:', err);
      setError('Error al registrar pago');
      throw err;
    }
  }, [lots]);

  // Marcar como devuelto
  const returnLot = useCallback(async (id: string) => {
    try {
      // Determinar IDs individuales si es grupo
      const ids = id.includes('-') ? id.split('-').map(s => s.trim()) : [id];
      // Si es un grupo (id con guiones), eliminar el documento de grupo
      if (id.includes('-')) {
        await deleteDoc(doc(db, 'lots', id));
      }
      // Limpiar todos los documentos individuales
      for (const lotId of ids) {
        const lotRef = doc(db, 'lots', lotId);
        // Verificar si existe el documento individual
        const lotSnap = await getDocs(collection(db, 'lots'));
        const lotData = lotSnap.docs.find(d => d.id === lotId)?.data();
        if (lotData) {
          await updateDoc(lotRef, {
            status: 'disponible',
            buyerName: '',
            buyerDPI: '',
            phoneNumber: '',
            telefono: '',
            depositSlip: '',
            receiptNumber: '',
            saleDate: '',
            fechaVenta: '',
            paymentType: '',
            amountPaid: 0,
            payments: [],
            comprador: '',
            dpi: '',
            enganche: '',
            abonosRealizados: '',
            recibo: '',
            idLotes: [],
            valorTotal: '',
            totalValue: 0,
            valorLote: '',
            lotValue: 0,
            numeroCuotas: '',
            numberOfPayments: 0,
            numPayments: 0,
            observaciones: '',
            tipoVenta: '',
            seller: '',
            vendedor: '',
            boletaPago: '',
            reciboCaja: '',
            saldoActual: '',
            createdAt: '',
            documents: [],
          });
        }
      }
      // 1. Obtener snapshot previo
      const lotRef = doc(db, 'lots', id);
      const prevSnap = await getDocs(collection(db, 'lots'));
      const prevLot = prevSnap.docs.find(d => d.id === id)?.data();
      if (prevLot) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const historyId = `${id}-${timestamp}`;
        await setDoc(doc(db, 'lotHistory', historyId), {
          lotId: id,
          ...prevLot,
          deletedAt: new Date().toISOString(),
          action: 'devolución',
        });
      }
      // 2. Eliminar movimientos automáticos relacionados (por grupo y por cada ID individual)
      const movimientosRef = collection(db, 'movimientos');
      const movimientosSnap = await getDocs(movimientosRef);
      const movimientosAEliminar: string[] = [];
      movimientosSnap.forEach((docu) => {
        const movimiento = docu.data();
        // Eliminar si el movimiento es automático y está relacionado con el grupo o cualquier ID individual
        if (movimiento.automatic && movimiento.lotes && (
          (typeof movimiento.lotes === 'string' && (movimiento.lotes === id || ids.includes(movimiento.lotes))) ||
          (Array.isArray(movimiento.lotes) && (movimiento.lotes.includes(id) || ids.some((i: string) => movimiento.lotes.includes(i))))
        )) {
          movimientosAEliminar.push(docu.id);
        }
      });
      for (const movimientoId of movimientosAEliminar) {
        await deleteDoc(doc(db, 'movimientos', movimientoId));
      }
      // 3. Eliminar comisiones asociadas (por grupo y por cada ID individual)
      const comisionesSnap = await getDocs(collection(db, 'comisiones'));
      const comisionesAEliminar: string[] = [];
      comisionesSnap.forEach((docu) => {
        const com = docu.data();
        if (
          com.lotId === id ||
          (typeof com.lotNumber === 'string' && (
            com.lotNumber === id ||
            ids.some((i: string) => com.lotNumber.split(/,|-/).map((s: string) => s.trim()).includes(i))
          ))
        ) {
          comisionesAEliminar.push(docu.id);
        }
      });
      for (const comisionId of comisionesAEliminar) {
        await deleteDoc(doc(db, 'comisiones', comisionId));
      }
      // 4. Eliminar cuotas/cobros asociados (por grupo y por cada ID individual)
      const cobroIds = [id, ...ids.filter(i => i !== id)];
      for (const cobroId of cobroIds) {
        const cobrosRef = doc(db, 'cobros', cobroId);
        const cuotasSnap = await getDocs(collection(cobrosRef, 'cuotas'));
        for (const cuotaDoc of cuotasSnap.docs) {
          await deleteDoc(doc(cobrosRef, 'cuotas', cuotaDoc.id));
        }
      }
    } catch (err) {
      console.error('Error returning lot:', err);
      setError('Error al devolver lote');
      throw err;
    }
  }, []);

  return {
    lots,
    loading,
    error,
    fetchLots,
    addLot,
    updateLot,
    deleteLot,
    addPayment,
    returnLot,
  };
}

export function useLotHistory(lotId: string | undefined) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lotId) {
      setHistory([]);
      return;
    }
    setLoading(true);
    const fetchHistory = async () => {
      try {
        const q = query(collection(db, 'lotHistory'), where('lotId', '==', lotId));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Ordenar por deletedAt descendente (más reciente primero)
        data.sort((a, b) => ((b as any).deletedAt || '').localeCompare((a as any).deletedAt || ''));
        setHistory(data);
        setError(null);
      } catch (err) {
        setError('Error al consultar historial del lote');
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [lotId]);

  return { history, loading, error };
} 